import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Checkbox, Divider, Spin, Tag, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { PermissionAction } from "../../flatbuffers/generated/archive/permission-action";
import type { RoleScopeGrants, ScopeGrant } from "../../redux/scope/scopeSlice";
import useGetAllBanksLight from "../../customHooks/GET_requests/useGetAllBanksLight";
import { useLazyGetBankQuery } from "../../redux/banks/bankAPI";
import GETJwtToken from "../../redux/authentication/authentication";
import formatUuidT from "../../flatbuffers/display/uuid";
import type { BankInterface, BankFloorInterface } from "../../interfaces/Bank.interface";
import type { LiftInterface } from "../../interfaces/Lift.interface";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Props {
  value: RoleScopeGrants;
  onChange: (next: RoleScopeGrants) => void;
  orgId: string;
}

/**
 * A node in the tree grid state maps entity UUIDs to a set of enabled actions.
 * This is an intermediate UI representation that gets converted to/from
 * RoleScopeGrants on load/save.
 */
interface GridState {
  bankActions: Record<string, Set<PermissionAction>>;
  liftActions: Record<string, Set<PermissionAction>>;
  floorActions: Record<string, Set<PermissionAction>>;
}

const ALL_ACTIONS: PermissionAction[] = [
  PermissionAction.VIEW,
  PermissionAction.CREATE,
  PermissionAction.UPDATE,
  PermissionAction.DELETE,
  PermissionAction.ACTION,
];

// ---------------------------------------------------------------------------
// Conversion helpers (grantsToGridState / gridStateToGrants)
// ---------------------------------------------------------------------------

function accumGrants(
  map: Record<string, Set<PermissionAction>>,
  grant: ScopeGrant,
): Record<string, Set<PermissionAction>> {
  const existing = map[grant.entityId];
  const updated = new Set(existing);
  updated.add(grant.action);
  return { ...map, [grant.entityId]: updated };
}

function grantsToGridState(grants: RoleScopeGrants): GridState {
  return {
    bankActions: grants.bankGrants.reduce<Record<string, Set<PermissionAction>>>(accumGrants, {}),
    liftActions: grants.liftGrants.reduce<Record<string, Set<PermissionAction>>>(accumGrants, {}),
    floorActions: grants.floorGrants.reduce<Record<string, Set<PermissionAction>>>(accumGrants, {}),
  };
}

function actionsToGrants(entityId: string, actions: Set<PermissionAction>): ScopeGrant[] {
  return Array.from(actions).map((action) => ({ entityId, action }));
}

function gridStateToGrants(grid: GridState): RoleScopeGrants {
  return {
    bankGrants: Object.entries(grid.bankActions).flatMap(([id, actions]) =>
      actionsToGrants(id, actions),
    ),
    liftGrants: Object.entries(grid.liftActions).flatMap(([id, actions]) =>
      actionsToGrants(id, actions),
    ),
    floorGrants: Object.entries(grid.floorActions).flatMap(([id, actions]) =>
      actionsToGrants(id, actions),
    ),
  };
}

// ---------------------------------------------------------------------------
// Label helpers
// ---------------------------------------------------------------------------

function permissionActionLabel(action: PermissionAction, t: (key: string) => string): string {
  if (action === PermissionAction.VIEW) return t("general.scopeActionView");
  if (action === PermissionAction.CREATE) return t("general.scopeActionCreate");
  if (action === PermissionAction.UPDATE) return t("general.scopeActionUpdate");
  if (action === PermissionAction.DELETE) return t("general.scopeActionDelete");
  if (action === PermissionAction.ACTION) return t("general.scopeActionAction");
  return String(action);
}

function scopeClassLabelBank(hasRows: boolean, t: (key: string) => string): string {
  return hasRows ? t("general.scopeClassRestrictedBanks") : t("general.scopeClassAllBanks");
}

function scopeClassLabelLift(hasRows: boolean, t: (key: string) => string): string {
  return hasRows ? t("general.scopeClassRestrictedLifts") : t("general.scopeClassAllLifts");
}

function scopeClassLabelFloor(hasRows: boolean, t: (key: string) => string): string {
  return hasRows ? t("general.scopeClassRestrictedFloors") : t("general.scopeClassAllFloors");
}

// ---------------------------------------------------------------------------
// Action checkbox row (reusable)
// ---------------------------------------------------------------------------

interface ActionRowProps {
  entityId: string;
  currentActions: Set<PermissionAction>;
  onToggle: (entityId: string, action: PermissionAction, checked: boolean) => void;
  t: (key: string) => string;
}

function ActionRow({ entityId, currentActions, onToggle, t }: ActionRowProps) {
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {ALL_ACTIONS.map((action) => (
        <Checkbox
          key={action}
          checked={currentActions.has(action)}
          onChange={(e) => onToggle(entityId, action, e.target.checked)}
        >
          {permissionActionLabel(action, t)}
        </Checkbox>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bank lazy-loaded detail row
// ---------------------------------------------------------------------------

interface BankDetailRowProps {
  bankId: string;
  grid: GridState;
  onToggle: (
    entityClass: "bank" | "lift" | "floor",
    entityId: string,
    action: PermissionAction,
    checked: boolean,
  ) => void;
  onApplyAllLifts: (liftIds: string[], actions: Set<PermissionAction>) => void;
  onApplyAllFloors: (floorIds: string[], actions: Set<PermissionAction>) => void;
  t: (key: string) => string;
}

function BankDetailRow({
  bankId,
  grid,
  onToggle,
  onApplyAllLifts,
  onApplyAllFloors,
  t,
}: BankDetailRowProps) {
  const [bank, setBank] = useState<BankInterface | null>(null);
  const [loading, setLoading] = useState(false);
  const [getBank] = useLazyGetBankQuery();

  useEffect(() => {
    let cancelled = false;
    async function fetchBank() {
      setLoading(true);
      try {
        const token = await GETJwtToken();
        const result = await getBank({ bankId, token }).unwrap();
        if (!cancelled) {
          setBank(result?.data ?? null);
        }
      } catch {
        // fetch failure is non-fatal — just leave bank as null
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchBank();
    return () => {
      cancelled = true;
    };
  }, [bankId, getBank]);

  const bankActions = grid.bankActions[bankId] ?? new Set<PermissionAction>();
  const lifts: LiftInterface[] = (bank?.lifts as LiftInterface[] | undefined) ?? [];
  const bankFloors: BankFloorInterface[] =
    (bank?.bankFloors as BankFloorInterface[] | undefined) ?? [];

  const handleApplyLifts = useCallback(() => {
    const liftIds = lifts.map((lift) => formatUuidT(lift.id)).filter(Boolean);
    onApplyAllLifts(liftIds, bankActions);
  }, [onApplyAllLifts, lifts, bankActions]);

  const handleApplyFloors = useCallback(() => {
    const floorIds = bankFloors.map((floor) => floor.id).filter(Boolean);
    onApplyAllFloors(floorIds, bankActions);
  }, [onApplyAllFloors, bankFloors, bankActions]);

  const handleBankToggle = useCallback(
    (id: string, action: PermissionAction, checked: boolean) => {
      onToggle("bank", id, action, checked);
    },
    [onToggle],
  );

  const handleLiftToggle = useCallback(
    (id: string, action: PermissionAction, checked: boolean) => {
      onToggle("lift", id, action, checked);
    },
    [onToggle],
  );

  const handleFloorToggle = useCallback(
    (id: string, action: PermissionAction, checked: boolean) => {
      onToggle("floor", id, action, checked);
    },
    [onToggle],
  );

  return (
    <div style={{ marginBottom: 8 }}>
      <ActionRow entityId={bankId} currentActions={bankActions} onToggle={handleBankToggle} t={t} />
      {loading && <Spin size="small" style={{ marginTop: 4 }} />}
      {!loading && bank && lifts.length > 0 && (
        <div style={{ marginTop: 8, paddingLeft: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 500 }}>Lifts</span>
            <Tooltip title={t("general.scopeApplyAllLifts")}>
              <Button size="small" onClick={handleApplyLifts}>
                Apply bank actions to all lifts
              </Button>
            </Tooltip>
          </div>
          {lifts.map((lift) => {
            const liftId = formatUuidT(lift.id);
            if (!liftId) return null;
            const liftName = String(lift.name ?? liftId.substring(0, 8));
            return (
              <div key={liftId} style={{ marginBottom: 4 }}>
                <div style={{ fontSize: 12, color: "#888", marginBottom: 2 }}>{liftName}</div>
                <ActionRow
                  entityId={liftId}
                  currentActions={grid.liftActions[liftId] ?? new Set<PermissionAction>()}
                  onToggle={handleLiftToggle}
                  t={t}
                />
              </div>
            );
          })}
        </div>
      )}
      {!loading && bank && bankFloors.length > 0 && (
        <div style={{ marginTop: 8, paddingLeft: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 500 }}>Floors</span>
            <Tooltip title={t("general.scopeApplyAllFloors")}>
              <Button size="small" onClick={handleApplyFloors}>
                Apply bank actions to all floors
              </Button>
            </Tooltip>
          </div>
          {bankFloors.map((floor) => {
            const floorId = floor.id;
            if (!floorId) return null;
            const floorName = floor.name || String(floor.floorIndex) || floorId.substring(0, 8);
            return (
              <div key={floorId} style={{ marginBottom: 4 }}>
                <div style={{ fontSize: 12, color: "#888", marginBottom: 2 }}>{floorName}</div>
                <ActionRow
                  entityId={floorId}
                  currentActions={grid.floorActions[floorId] ?? new Set<PermissionAction>()}
                  onToggle={handleFloorToggle}
                  t={t}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main editor component
// ---------------------------------------------------------------------------

/**
 * Controlled scope editor for a role's entity-scoped permission grants.
 *
 * Displays banks, and lazily loads lifts + floors per bank.
 * Allows toggling individual PermissionAction values per entity.
 * Emits the updated RoleScopeGrants via onChange whenever the user makes
 * a change.
 *
 * Design invariants:
 * - Empty grants for a class = allow-all (prominently labeled as "no restriction").
 * - All 5 PermissionAction values are shown per entity node.
 * - No nested ternaries — helper functions used for all conditional logic.
 */
export default function RoleScopeEditor({ value, onChange, orgId }: Props) {
  const [t] = useTranslation("global");
  const { banksLight, isLoadingGetAllBanksLight } = useGetAllBanksLight(orgId);
  const [expandedBanks, setExpandedBanks] = useState<Set<string>>(new Set());

  const grid = useMemo(() => grantsToGridState(value), [value]);

  const hasBankRows = Object.keys(grid.bankActions).length > 0;
  const hasLiftRows = Object.keys(grid.liftActions).length > 0;
  const hasFloorRows = Object.keys(grid.floorActions).length > 0;

  function emitChange(next: GridState) {
    onChange(gridStateToGrants(next));
  }

  const handleToggle = useCallback(
    (
      entityClass: "bank" | "lift" | "floor",
      entityId: string,
      action: PermissionAction,
      checked: boolean,
    ) => {
      const next: GridState = {
        bankActions: { ...grid.bankActions },
        liftActions: { ...grid.liftActions },
        floorActions: { ...grid.floorActions },
      };

      function applyToggle(map: Record<string, Set<PermissionAction>>) {
        const current = map[entityId] ? new Set(map[entityId]) : new Set<PermissionAction>();
        if (checked) {
          current.add(action);
        } else {
          current.delete(action);
        }
        if (current.size === 0) {
          // Remove entity from map when no actions remain — no rows = allow-all
          const updated = { ...map };
          delete updated[entityId];
          return updated;
        }
        return { ...map, [entityId]: current };
      }

      if (entityClass === "bank") {
        next.bankActions = applyToggle(grid.bankActions);
      } else if (entityClass === "lift") {
        next.liftActions = applyToggle(grid.liftActions);
      } else {
        next.floorActions = applyToggle(grid.floorActions);
      }

      emitChange(next);
    },
    [grid, emitChange],
  );

  const handleApplyAllLifts = useCallback(
    (liftIds: string[], actions: Set<PermissionAction>) => {
      if (liftIds.length === 0) return;
      const nextLiftActions = liftIds.reduce<Record<string, Set<PermissionAction>>>(
        (acc, liftId) => {
          if (actions.size === 0) {
            const { [liftId]: _removed, ...rest } = acc;
            return rest;
          }
          return { ...acc, [liftId]: new Set(actions) };
        },
        { ...grid.liftActions },
      );
      emitChange({
        bankActions: { ...grid.bankActions },
        liftActions: nextLiftActions,
        floorActions: { ...grid.floorActions },
      });
    },
    [grid, emitChange],
  );

  const handleApplyAllFloors = useCallback(
    (floorIds: string[], actions: Set<PermissionAction>) => {
      if (floorIds.length === 0) return;
      const nextFloorActions = floorIds.reduce<Record<string, Set<PermissionAction>>>(
        (acc, floorId) => {
          if (actions.size === 0) {
            const { [floorId]: _removed, ...rest } = acc;
            return rest;
          }
          return { ...acc, [floorId]: new Set(actions) };
        },
        { ...grid.floorActions },
      );
      emitChange({
        bankActions: { ...grid.bankActions },
        liftActions: { ...grid.liftActions },
        floorActions: nextFloorActions,
      });
    },
    [grid, emitChange],
  );

  const toggleBankExpand = useCallback((bankId: string) => {
    setExpandedBanks((prev) => {
      const next = new Set(prev);
      if (next.has(bankId)) {
        next.delete(bankId);
      } else {
        next.add(bankId);
      }
      return next;
    });
  }, []);

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <strong>{t("general.scopeEditorTitle")}</strong>
        <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
          {t("general.scopeEditorSubtitle")}
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <Tag color={hasBankRows ? "orange" : "green"}>{scopeClassLabelBank(hasBankRows, t)}</Tag>
        <Tag color={hasLiftRows ? "orange" : "green"}>{scopeClassLabelLift(hasLiftRows, t)}</Tag>
        <Tag color={hasFloorRows ? "orange" : "green"}>{scopeClassLabelFloor(hasFloorRows, t)}</Tag>
      </div>

      {isLoadingGetAllBanksLight && <Spin />}

      {!isLoadingGetAllBanksLight && banksLight.length === 0 && (
        <Alert
          type="info"
          showIcon
          message="No banks found. Configure banks before setting scope restrictions."
        />
      )}

      {banksLight.map((bankLight) => {
        // bankLight.id decodes to a FlatBuffer UuidT object, not a string, so
        // String(...) would yield "[object Object]" for every bank — collapsing
        // all React keys and all bank scope entityIds to one value.
        const bankId = formatUuidT(bankLight.id);
        if (!bankId) return null;
        const bankName = String(bankLight.name ?? bankId.substring(0, 8));
        const isExpanded = expandedBanks.has(bankId);
        const bankHasActions = !!grid.bankActions[bankId];

        return (
          <BankRow
            key={bankId}
            bankId={bankId}
            bankName={bankName}
            isExpanded={isExpanded}
            bankHasActions={bankHasActions}
            grid={grid}
            onToggle={handleToggle}
            onApplyAllLifts={handleApplyAllLifts}
            onApplyAllFloors={handleApplyAllFloors}
            onToggleExpand={toggleBankExpand}
            t={t}
          />
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// BankRow — one bank header + optional detail (extracted to avoid jsx-no-bind)
// ---------------------------------------------------------------------------

interface BankRowProps {
  bankId: string;
  bankName: string;
  isExpanded: boolean;
  bankHasActions: boolean;
  grid: GridState;
  onToggle: (
    entityClass: "bank" | "lift" | "floor",
    entityId: string,
    action: PermissionAction,
    checked: boolean,
  ) => void;
  onApplyAllLifts: (liftIds: string[], actions: Set<PermissionAction>) => void;
  onApplyAllFloors: (floorIds: string[], actions: Set<PermissionAction>) => void;
  onToggleExpand: (bankId: string) => void;
  t: (key: string) => string;
}

function BankRow({
  bankId,
  bankName,
  isExpanded,
  bankHasActions,
  grid,
  onToggle,
  onApplyAllLifts,
  onApplyAllFloors,
  onToggleExpand,
  t,
}: BankRowProps) {
  const handleClick = useCallback(() => {
    onToggleExpand(bankId);
  }, [onToggleExpand, bankId]);

  return (
    <div style={{ marginBottom: 8 }}>
      <Divider orientation="left" style={{ marginBottom: 4, marginTop: 4 }}>
        <Button
          type="link"
          size="small"
          style={{ padding: 0, fontWeight: bankHasActions ? 600 : 400 }}
          onClick={handleClick}
        >
          {bankName} {isExpanded ? "▲" : "▼"}
        </Button>
      </Divider>
      {isExpanded && (
        <BankDetailRow
          bankId={bankId}
          grid={grid}
          onToggle={onToggle}
          onApplyAllLifts={onApplyAllLifts}
          onApplyAllFloors={onApplyAllFloors}
          t={t}
        />
      )}
    </div>
  );
}
